/**
 * Cart spend for progress bar tiers (cents).
 * Counts paid lines only — shipping-bar and gift-block free gifts are excluded entirely
 * (their price + any "free gift" discount must not inflate the bar).
 * Uses original_line_price so automatic discounts on paid items still count.
 */
function getProgressBarCartTotal(cart) {
    if (!cart) return 0;

    const isAutoGift = (item) => {
        const props = item?.properties || {};
        return props._shipping_bar_gift === '1' || props._shipping_bar_gift === 1
            || props._gift_block_cart === '1' || props._gift_block_cart === 1;
    };

    let total = 0;
    if (cart.items?.length) {
        cart.items.forEach((item) => {
            if (isAutoGift(item)) return;
            total += Number(item.original_line_price) || Number(item.final_line_price) || Number(item.line_price) || 0;
        });
    }

    return total;
}
window.getProgressBarCartTotal = getProgressBarCartTotal;

let shippingBarGiftSyncLock = false;

if (!customElements.get('shipping-bar')) {
    customElements.define('shipping-bar', class ShippingBar extends HTMLElement {
        static get observedAttributes() {
            return ['data-cart-totals'];
        }

        connectedCallback() {
            this.update();
            this._onResize = () => {
                if (typeof this.setBar === 'function') this.setBar();
            };
            window.addEventListener('resize', this._onResize);
            setTimeout(() => {
                if (typeof this.syncShippingBarGifts === 'function') {
                    this.syncShippingBarGifts();
                }
            }, 500);
        }

        disconnectedCallback() {
            if (this._onResize) {
                window.removeEventListener('resize', this._onResize);
            }
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'data-cart-totals' && oldValue !== newValue) {
                this.update();
            }
        }

        update() {
            this.calculateAndRender();
        }

        calculateAndRender() {
            const milestones = this.getMilestones();
            if (milestones.length === 0) {
                return;
            }

            const dataRate = parseFloat(this.dataset.amount);
            const amountCalc = !isNaN(dataRate) && dataRate > 0
                ? dataRate
                : Math.min(...milestones.map(m => m.price));
            const cartTotals = parseFloat(this.dataset.cartTotals) / 100;
            if (isNaN(cartTotals)) {
                return;
            }

            const amount = (amountCalc - cartTotals) * 100;
            const moneyFormat = this.dataset.moneyFormat || window.money_format;
            const nextMilestone = milestones.find(m => cartTotals < m.price);
            const allUnlocked = milestones.length > 0 && !nextMilestone;

            const cartContext = this.closest('cart-slide') || this.closest('.main-cart');
            const textContainer = cartContext
                ? cartContext.querySelector('[data-shipping-bar-text]')
                : document.querySelector('[data-shipping-bar-text]');
            const wrapper = this.closest('.shipping-bar-wrapper');

            if (textContainer) {
                if (!textContainer.dataset.template) {
                    textContainer.dataset.template = textContainer.innerHTML;
                }

                if (nextMilestone) {
                    this.classList.remove('shipping-unlocked');
                    wrapper?.classList.remove('shipping-unlocked');
                    const remainingCents = Math.max(0, (nextMilestone.price - cartTotals) * 100);
                    const formattedAmount = Shopify.formatMoney(remainingCents, moneyFormat);
                    const template = nextMilestone.progressText || textContainer.dataset.template;
                    textContainer.innerHTML = this.buildMessageFromTemplate(template, formattedAmount, nextMilestone.label);
                    textContainer.style.fontWeight = '400';
                } else if (allUnlocked || amount <= 0) {
                    this.classList.add('shipping-unlocked');
                    wrapper?.classList.add('shipping-unlocked');
                    textContainer.textContent = this.dataset.shippingMessage || '';
                    textContainer.style.fontWeight = '700';
                } else {
                    this.classList.remove('shipping-unlocked');
                    wrapper?.classList.remove('shipping-unlocked');
                    const base = textContainer.dataset.template;
                    textContainer.innerHTML = base.replace(/\|\|amount\|\|/g, Shopify.formatMoney(amount, moneyFormat));
                    textContainer.style.fontWeight = '400';
                }
            }

            this.threshold = amountCalc;
            this.cartTotals = cartTotals;

            this.setBar();
            this.syncShippingBarGifts();
        }

        updateCartTotals(cartTotalPrice) {
            if (cartTotalPrice === undefined || cartTotalPrice === null) {
                return;
            }
            this.dataset.cartTotals = String(cartTotalPrice);
        }

        updateFromCart(cart) {
            const goalChanged = this.applyShadowFlowGoal(cart);
            const before = this.dataset.cartTotals;
            this.updateCartTotals(getProgressBarCartTotal(cart));
            if (goalChanged && before === this.dataset.cartTotals) this.update();
        }

        // Shadow Flow has its own shipping profile (free at $50): a cart holding only Shadow Flow uses that goal.
        // Mirrors snippets/shipping-bar-milestones.liquid. Returns true when a goal changed.
        applyShadowFlowGoal(cart) {
            const paid = (cart?.items || []).filter((item) => {
                const props = item?.properties || {};
                return !(props._shipping_bar_gift == 1 || props._gift_block_cart == 1);
            });
            const sfOnly = paid.length > 0 && paid.every((item) => item.handle === 'shadow-flow-calm-and-relaxation-formula');
            let changed = false;
            this.querySelectorAll('.cart-slide__label[data-goal-type="shipping"][data-default-price]').forEach((label) => {
                const goal = sfOnly ? '50' : label.dataset.defaultPrice;
                if (!goal || label.dataset.price === goal) return;
                label.dataset.price = goal;
                const spend = label.querySelector('.cart-slide__label-spend');
                if (spend) spend.textContent = 'Spend ' + Shopify.formatMoney(Number(goal) * 100, this.dataset.moneyFormat || window.money_format);
                changed = true;
            });
            if (changed) {
                const prices = Array.from(this.querySelectorAll('.cart-slide__label[data-price]')).map((l) => parseFloat(l.dataset.price)).filter((p) => p > 0);
                if (prices.length) this.dataset.amount = String(Math.min(...prices));
            }
            return changed;
        }

        setBar() {
            const element = this.querySelector('[data-progress-span]');
            if (!element) return;

            if (!this.threshold || this.threshold <= 0) return;

            const marks = this.querySelectorAll('.cart-slide__label');
            const maxThreshold = this.getMaxThreshold(marks);
            if (!maxThreshold || maxThreshold <= 0) return;

            this.updateReachedLabels();
            // Place icons first, then fill must follow the same visual positions
            this.positionIcons();

            let value = this.getFillPercentFromIcons(marks);
            if (value > 100) value = 100;
            if (value < 0) value = 0;

            element.setAttribute('data-value', value.toFixed(2));
            element.style.setProperty('--bar-width', `${value.toFixed(2)}%`);
            element.textContent = value.toFixed(2);
        }

        /**
         * Fill % based on actual icon centers so the line matches milestones
         * (including first/last pins and 80px min-gap shifts).
         */
        getFillPercentFromIcons(marks) {
            const barWidth = this.offsetWidth || 1;
            const cartTotals = this.cartTotals || 0;
            if (cartTotals <= 0 || barWidth <= 0) return 0;

            const barLeft = this.getBoundingClientRect().left;
            const points = Array.from(marks)
                .map((mark) => {
                    const price = parseFloat(mark.dataset.price);
                    const icon = mark.querySelector('i') || mark;
                    const rect = icon.getBoundingClientRect();
                    return {
                        price,
                        center: rect.left + rect.width / 2 - barLeft
                    };
                })
                .filter((p) => !isNaN(p.price) && p.price > 0)
                .sort((a, b) => a.price - b.price);

            if (points.length === 0) {
                const fallbackMax = this.getMaxThreshold(marks) || 1;
                return Math.min(100, (cartTotals / fallbackMax) * 100);
            }

            const first = points[0];
            const last = points[points.length - 1];

            if (cartTotals >= last.price) return 100;

            if (cartTotals <= first.price) {
                const pct = first.center * (cartTotals / first.price);
                return Math.max(0, Math.min(100, (pct / barWidth) * 100));
            }

            for (let i = 0; i < points.length - 1; i++) {
                const a = points[i];
                const b = points[i + 1];
                if (cartTotals >= a.price && cartTotals <= b.price) {
                    const span = Math.max(0.0001, b.price - a.price);
                    const ratio = (cartTotals - a.price) / span;
                    const fillPx = a.center + ratio * (b.center - a.center);
                    return Math.max(0, Math.min(100, (fillPx / barWidth) * 100));
                }
            }

            return Math.min(100, (cartTotals / last.price) * 100);
        }

        updateReachedLabels() {
            const labels = this.querySelectorAll('.cart-slide__label');
            labels.forEach(label => {
                const threshold = parseFloat(label.dataset.price);
                if (isNaN(threshold) || threshold <= 0) {
                    label.classList.remove('is-reached');
                    return;
                }

                label.classList.toggle('is-reached', this.cartTotals >= threshold);
            });
        }

        getMaxThreshold(labels) {
            let maxThreshold = this.threshold;
            labels.forEach(label => {
                const price = parseFloat(label.dataset.price);
                if (!isNaN(price) && price > maxThreshold) {
                    maxThreshold = price;
                }
            });
            return maxThreshold;
        }

        getMinThreshold(labels) {
            let minThreshold = NaN;
            labels.forEach(label => {
                const price = parseFloat(label.dataset.price);
                if (!isNaN(price) && price > 0) {
                    minThreshold = isNaN(minThreshold) ? price : Math.min(minThreshold, price);
                }
            });
            if (isNaN(minThreshold)) {
                minThreshold = parseFloat(this.threshold) || 0;
            }
            return minThreshold;
        }

        getTrackLayout() {
            const barWidth = this.offsetWidth || 1;
            const insetPx = 18;
            const leftPct = Math.min(12, (insetPx / barWidth) * 100);
            const rightPct = Math.min(14, (insetPx / barWidth) * 100);
            return {
                leftPct,
                rightPct,
                rangePct: Math.max(0, 100 - leftPct - rightPct)
            };
        }

        positionOnTrack(price, minThreshold, maxThreshold) {
            const { leftPct, rangePct } = this.getTrackLayout();
            if (maxThreshold <= minThreshold) {
                return leftPct + rangePct;
            }
            const ratio = (price - minThreshold) / (maxThreshold - minThreshold);
            return leftPct + Math.max(0, Math.min(1, ratio)) * rangePct;
        }

        fillOnTrack(cartTotals, minThreshold, maxThreshold) {
            const { leftPct, rangePct } = this.getTrackLayout();
            if (cartTotals <= 0) return 0;
            if (maxThreshold <= minThreshold) {
                return (cartTotals / maxThreshold) * 100;
            }
            if (cartTotals < minThreshold) {
                return leftPct * (cartTotals / minThreshold);
            }
            const ratio = (cartTotals - minThreshold) / (maxThreshold - minThreshold);
            return leftPct + Math.min(1, ratio) * rangePct;
        }

        getMilestones() {
            return Array.from(this.querySelectorAll('.cart-slide__label'))
                .map(label => {
                    const price = parseFloat(label.dataset.price);
                    const titleEl = label.querySelector('.cart-slide__label-title');
                    const labelText = titleEl?.textContent?.trim()
                        || label.querySelector('p')?.textContent?.trim()
                        || '';
                    const goalType = (label.dataset.goalType || '').toLowerCase();
                    const order = parseInt(label.dataset.order, 10) || 999;
                    const progressText = label.dataset.progressText || '';
                    const giftVariantId = (label.dataset.giftVariantId || '').trim();
                    return { price, label: labelText, goalType, order, progressText, giftVariantId };
                })
                .filter(item => !isNaN(item.price) && item.price > 0)
                .sort((a, b) => a.order - b.order || a.price - b.price);
        }

        buildMessageFromTemplate(template, formattedAmount, rewardLabel) {
            const safeTemplate = (template || '').trim();
            if (!safeTemplate) {
                return `You’re ${formattedAmount} away from ${rewardLabel || 'next reward'}`;
            }
            return safeTemplate
                .replace(/\|\|amount\|\|/g, formattedAmount)
                .replace(/\|\|label\|\|/g, rewardLabel || '');
        }

        getCartState() {
            return (this.closest('cart-slide') && this.closest('cart-slide').cartState) || window.VanillaCartState || null;
        }

        getShippingGiftStorageKey(variantId) {
            return `shippingBarGiftAdded_${variantId}`;
        }

        getShippingGiftFlag(variantId) {
            try {
                return sessionStorage.getItem(this.getShippingGiftStorageKey(variantId)) === '1';
            } catch (e) {
                return false;
            }
        }

        setShippingGiftFlag(variantId) {
            try {
                sessionStorage.setItem(this.getShippingGiftStorageKey(variantId), '1');
            } catch (e) { }
        }

        clearShippingGiftFlag(variantId) {
            try {
                sessionStorage.removeItem(this.getShippingGiftStorageKey(variantId));
            } catch (e) { }
        }

        isShippingBarGiftLine(item, variantId) {
            if (!item) return false;
            const sameVariant = String(item.variant_id) === String(variantId) || String(item.id) === String(variantId);
            if (!sameVariant) return false;
            const props = item.properties || {};
            return props._shipping_bar_gift === '1' || props._shipping_bar_gift === 1;
        }

        getGiftMilestones() {
            const fromLabels = this.getMilestones().filter(m => m.goalType === 'gift' && m.giftVariantId);
            if (fromLabels.length) return fromLabels;

            try {
                const parsed = JSON.parse(this.dataset.gifts || '[]');
                if (!Array.isArray(parsed)) return [];
                return parsed
                    .map((item) => ({
                        price: parseFloat(item.price),
                        giftVariantId: String(item.variantId || '').trim(),
                        goalType: 'gift'
                    }))
                    .filter((item) => item.giftVariantId && !isNaN(item.price) && item.price > 0);
            } catch (e) {
                return [];
            }
        }

        async syncShippingBarGifts() {
            if (shippingBarGiftSyncLock) return;

            const gifts = this.getGiftMilestones();
            if (gifts.length === 0) return;

            shippingBarGiftSyncLock = true;
            try {
                for (const gift of gifts) {
                    if (this.cartTotals >= gift.price) {
                        await this.addShippingBarGiftOnce(gift.giftVariantId);
                    } else {
                        await this.removeShippingBarGift(gift.giftVariantId);
                    }
                }
            } finally {
                shippingBarGiftSyncLock = false;
            }
        }

        async addShippingBarGiftOnce(variantId) {
            const numericId = parseInt(String(variantId), 10);
            if (!numericId) return;

            try {
                let existingItems = window.VanillaCartState?.cart?.items || [];
                if (!existingItems.length) {
                    const cartRes = await fetch('/cart.js');
                    if (cartRes.ok) {
                        const liveCart = await cartRes.json();
                        existingItems = liveCart.items || [];
                    }
                }
                const hasGiftAlready = existingItems.some(item => this.isShippingBarGiftLine(item, numericId));
                if (hasGiftAlready) return;

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        items: [{
                            id: numericId,
                            quantity: 1,
                            properties: { _shipping_bar_gift: '1' }
                        }]
                    })
                });

                if (!response.ok) {
                    const error = await response.json().catch(() => ({}));
                    throw new Error(error.description || error.message || 'Failed to add gift');
                }

                if (window.VanillaCartState && typeof window.VanillaCartState.fetchCart === 'function') {
                    await window.VanillaCartState.fetchCart();
                }
            } catch (error) {
                console.error('Shipping bar gift auto-add failed:', error);
            }
        }

        async removeShippingBarGift(variantId) {
            const cartState = this.getCartState();
            if (!cartState || typeof cartState.updateItemByLine !== 'function' || typeof cartState.fetchCart !== 'function') return;

            const numericId = parseInt(String(variantId), 10);
            if (!numericId) return;

            try {
                const cart = await cartState.fetchCart();
                const linesToRemove = (cart.items || [])
                    .map((item, index) => ({ item, line: index + 1 }))
                    .filter(({ item }) => this.isShippingBarGiftLine(item, numericId));

                for (const { line } of [...linesToRemove].reverse()) {
                    await cartState.updateItemByLine(line, 0);
                }
            } catch (error) {
                console.error('Shipping bar gift auto-remove failed:', error);
            }
        }

        positionIcons() {
            const marks = Array.from(this.querySelectorAll('.cart-slide__label'));
            if (marks.length === 0) return;

            const maxThreshold = this.getMaxThreshold(marks);
            const minThreshold = this.getMinThreshold(marks);
            if (!maxThreshold || maxThreshold <= 0) return;

            const sorted = [...marks].sort(
                (a, b) => (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0)
            );
            const firstMark = sorted[0];
            const lastMark = sorted[sorted.length - 1];
            const minGap = 80;
            const barWidth = this.offsetWidth || 1;

            // 1st + last: exact same as before — do not move
            sorted.forEach((mark) => {
                const textEl = mark.querySelector('.cart-slide__label-text');
                mark.style.position = 'absolute';
                mark.style.top = '0px';
                mark.style.right = 'auto';
                mark.style.left = '';
                mark.style.transform = '';
                if (textEl) {
                    textEl.style.transform = '';
                    textEl.style.textAlign = 'center';
                }
            });

            firstMark.style.left = '0';
            firstMark.style.right = 'auto';
            firstMark.style.transform = 'none';

            lastMark.style.left = 'auto';
            lastMark.style.right = '0';
            lastMark.style.transform = 'none';

            if (sorted.length === 1) {
                this.resolveLabelTextCollisions(sorted);
                return;
            }

            // Measure pinned end centers after layout
            const barLeft = this.getBoundingClientRect().left;
            const firstIcon = firstMark.querySelector('i') || firstMark;
            const lastIcon = lastMark.querySelector('i') || lastMark;
            const firstRect = firstIcon.getBoundingClientRect();
            const lastRect = lastIcon.getBoundingClientRect();
            const firstCenter = firstRect.left + firstRect.width / 2 - barLeft;
            const lastCenter = lastRect.left + lastRect.width / 2 - barLeft;
            const usable = Math.max(0, lastCenter - firstCenter);

            const prices = sorted.map((mark) => parseFloat(mark.dataset.price) || 0);
            const firstPrice = prices[0];
            const lastPrice = prices[prices.length - 1];
            const priceSpan = Math.max(0, lastPrice - firstPrice);

            // Inner marks only: spend-based, then enforce min 80px gap
            if (sorted.length > 2) {
                const centers = prices.map((price, index) => {
                    if (index === 0) return firstCenter;
                    if (index === prices.length - 1) return lastCenter;
                    if (priceSpan <= 0) {
                        return firstCenter + (usable * index) / (prices.length - 1);
                    }
                    const ratio = (price - firstPrice) / priceSpan;
                    return firstCenter + Math.max(0, Math.min(1, ratio)) * usable;
                });

                // Push inners right if too close to previous
                for (let i = 1; i < centers.length - 1; i++) {
                    centers[i] = Math.max(centers[i], centers[i - 1] + minGap);
                }

                // Pull inners left if too close to next / pinned last
                for (let i = centers.length - 2; i >= 1; i--) {
                    centers[i] = Math.min(centers[i], centers[i + 1] - minGap);
                    centers[i] = Math.max(centers[i], centers[i - 1] + minGap);
                }

                // If still can't fit min gaps between pinned ends, space inners evenly
                const needed = (sorted.length - 1) * minGap;
                if (needed > usable) {
                    for (let i = 1; i < centers.length - 1; i++) {
                        centers[i] = firstCenter + (usable * i) / (sorted.length - 1);
                    }
                }

                for (let i = 1; i < sorted.length - 1; i++) {
                    sorted[i].style.left = `${centers[i]}px`;
                    sorted[i].style.right = 'auto';
                    sorted[i].style.transform = 'translateX(-50%)';
                }
            }

            // Re-pin ends so nothing moved them
            firstMark.style.left = '0';
            firstMark.style.right = 'auto';
            firstMark.style.transform = 'none';
            lastMark.style.left = 'auto';
            lastMark.style.right = '0';
            lastMark.style.transform = 'none';

            this.resolveLabelTextCollisions(sorted);
        }

        resolveLabelTextCollisions(marks) {
            const sorted = [...marks].sort(
                (a, b) => (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0)
            );
            const gap = 8;

            for (let i = 1; i < sorted.length; i++) {
                const prevText = sorted[i - 1].querySelector('.cart-slide__label-text');
                const currText = sorted[i].querySelector('.cart-slide__label-text');
                if (!prevText || !currText) continue;

                const prevRect = prevText.getBoundingClientRect();
                const currRect = currText.getBoundingClientRect();
                if (currRect.left >= prevRect.right + gap) continue;

                const shift = prevRect.right + gap - currRect.left;
                const match = (currText.style.transform || '').match(/translateX\(([-\d.]+)px\)/);
                const existing = match ? parseFloat(match[1]) : 0;
                currText.style.transform = `translateX(${existing + shift}px)`;
            }
        }
    });
}

document.addEventListener('cart:updated', (e) => {
    const cart = e.detail;
    if (!cart) return;
    document.querySelectorAll('shipping-bar').forEach((bar) => {
        if (typeof bar.updateFromCart === 'function') {
            bar.updateFromCart(cart);
        }
    });
});
